import { useEffect, useRef, useState } from 'react';
import type { HostingPanelAlertsView } from '@tosho/contracts';
import { PrototypeIcon } from './PrototypeIcon';
import { BusinessLogo } from './BusinessLogo';

export function HostingPleskAlerts({
  organizationLabel = 'Organisation',
  organizationLogoUrl = null,
}: {
  readonly organizationLabel?: string;
  readonly organizationLogoUrl?: string | null;
}): React.JSX.Element {
  const [view, setView] = useState<HostingPanelAlertsView | null>(null),
    [failed, setFailed] = useState(false),
    [range, setRange] = useState({ first: 1, last: 1 });
  const track = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const controller = new AbortController();
    let active = true,
      pending = false;
    const read = async (): Promise<void> => {
      if (pending) return;
      pending = true;
      try {
        const response = await fetch('/api/hosting-plesk-alerts', {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error();
        const value = (await response.json()) as HostingPanelAlertsView;
        if (!value || !Array.isArray(value.items)) throw new Error();
        if (active) {
          setView({
            items: value.items.filter(
              (server: HostingPanelAlertsView['items'][number]) =>
                server.accessIsCurrent &&
                server.automaticUpdates === false &&
                (server.status === 'SECURITY_UPDATES' || server.status === 'UPDATE_AVAILABLE'),
            ),
          });
          setFailed(false);
        }
      } catch {
        if (active) setFailed(true);
      } finally {
        pending = false;
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), 60000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(timer);
    };
  }, []);
  function measure(): void {
    const list = track.current,
      firstCard = list?.firstElementChild as HTMLElement | null;
    if (!list || !firstCard || !view?.items.length) return;
    const step = (firstCard.nextElementSibling as HTMLElement | null)?.offsetLeft
      ? (firstCard.nextElementSibling as HTMLElement).offsetLeft - firstCard.offsetLeft
      : firstCard.getBoundingClientRect().width + 16;
    const first = Math.max(0, Math.round(list.scrollLeft / Math.max(step, 1)));
    const visible = Math.max(1, Math.floor((list.clientWidth + 16) / Math.max(step, 1)));
    setRange({ first: first + 1, last: Math.min(view.items.length, first + visible) });
  }
  useEffect(() => {
    measure();
    const list = track.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [view]);
  function move(direction: number): void {
    const list = track.current,
      card = list?.firstElementChild as HTMLElement | null;
    if (!list || !card) return;
    const step = card.getBoundingClientRect().width + 16;
    list.scrollBy({
      left: direction * step * Math.max(1, range.last - range.first + 1),
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
  if (!failed && view?.items.length === 0) return <></>;
  return (
    <section className="hosting-backup-catalog hosting-alert-carousel" aria-label="Alertes Plesk">
      <header>
        <div>
          <p className="dashboard-panel__eyebrow">Serveurs reliés</p>
          <h2>
            <PrototypeIcon name="circle-alert" /> Alertes Plesk
          </h2>
        </div>
        {!!view?.items.length && (
          <div className="hosting-alert-carousel__controls">
            <span aria-live="polite">
              {range.first}–{Math.min(range.last, view.items.length)} / {view.items.length}
            </span>
            <button
              type="button"
              aria-label="Serveurs précédents"
              disabled={range.first <= 1}
              onClick={() => move(-1)}
            >
              <PrototypeIcon name="chevron-down" className="is-left" />
            </button>
            <button
              type="button"
              aria-label="Serveurs suivants"
              disabled={range.last >= view.items.length}
              onClick={() => move(1)}
            >
              <PrototypeIcon name="chevron-down" className="is-right" />
            </button>
          </div>
        )}
      </header>
      {failed && (
        <p className="tosho-feedback is-error" role="alert">
          Les alertes ne peuvent pas être actualisées.{' '}
          {view?.items.length
            ? 'Le dernier relevé affiché est conservé.'
            : 'Leur état actuel est inconnu.'}
        </p>
      )}
      {view ? (
        <ul
          ref={track}
          className="hosting-plesk-alerts"
          tabIndex={0}
          aria-label="Serveurs Plesk"
          onScroll={measure}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
              event.preventDefault();
              move(event.key === 'ArrowRight' ? 1 : -1);
            }
          }}
        >
          {view.items.map((server) => {
            const owner = server.owner;
            const logo =
              owner?.kind === 'ORGANIZATION'
                ? organizationLogoUrl
                : owner?.hasLogo && owner.id
                  ? '/api/clients/' +
                    encodeURIComponent(owner.id) +
                    '/logo?v=' +
                    (owner.logoVersion ?? 0)
                  : null;
            const ownerName =
              owner?.kind === 'ORGANIZATION' ? organizationLabel : owner?.displayName;
            const message =
              server.status === 'SECURITY_UPDATES'
                ? `${server.missedSecurityUpdates} mise${server.missedSecurityUpdates === 1 ? '' : 's'} à jour de sécurité à installer`
                : `Mise à jour Plesk ${server.availableUpdate} à installer`;
            return (
              <li key={server.serverId}>
                <div className="hosting-plesk-alerts__alert is-error">
                  <PrototypeIcon name="circle-alert" />
                  <strong>{message}</strong>
                </div>
                <strong className="hosting-plesk-alerts__server">{server.serverLabel}</strong>
                {logo && ownerName && (
                  <div className="hosting-plesk-alerts__owner">
                    <BusinessLogo name={ownerName} logoUrl={logo} />
                  </div>
                )}
                <span>Installation automatique désactivée dans Plesk.</span>
                <span>Ouvrez Plesk → Outils et paramètres → Mises à jour pour l’installer.</span>
                <small>
                  {server.observedAt
                    ? 'Relevé du ' +
                      new Date(server.observedAt).toLocaleString('fr-FR', {
                        timeZone: 'Europe/Paris',
                      }) +
                      (server.accessIsCurrent ? '' : ' · historique')
                    : 'Aucun relevé enregistré'}
                </small>
              </li>
            );
          })}
        </ul>
      ) : (
        !failed && <p role="status">Chargement des alertes…</p>
      )}
    </section>
  );
}
